import Button from '@ui/Button'
import Field from '@ui/Field'
import Input from '@ui/Input'
import Sheet from '@ui/Sheet'
import type { MultiFactorResolver, User } from 'firebase/auth'
import { type FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  addPasswordToAccount,
  changePassword,
  createPasswordAccount,
  IdpError,
  type IdpErrorCode,
  reauthWithGoogle,
  resolveTotp,
  signOutIdp,
} from '@/auth/idp'

import OneTimeCodeField from './OneTimeCodeField'
import ReauthForm, { idpErrorKey } from './ReauthForm'

// The user is already signed in to POLF (often via Google), so neither mode
// starts a session: create ends at "check your email", change at "done".

export type PasswordSheetMode = 'create' | 'change'

type Step =
  | { kind: 'reauth' }
  | { kind: 'new-password'; user: User | null }
  | { kind: 'code'; resolver: MultiFactorResolver }
  | { kind: 'done' }

type Props = {
  open: boolean
  mode: PasswordSheetMode
  email: string
  /**
   * Create then adds the password to the Google account (re-checked with a
   * popup) instead of creating a second account, which Identity Platform
   * refuses for an email it already knows.
   */
  googleConnected?: boolean
  onClose: () => void
  /** Called once the password was created or changed. */
  onChanged: () => void
}

const PasswordSheet = ({
  open,
  mode,
  email,
  googleConnected = false,
  onClose,
  onChanged,
}: Props) => {
  const { t } = useTranslation()
  const initial: Step =
    mode === 'create'
      ? { kind: 'new-password', user: null }
      : { kind: 'reauth' }
  const [step, setStep] = useState<Step>(initial)
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const linking = mode === 'create' && googleConnected
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<IdpErrorCode | null>(null)

  useEffect(() => {
    if (open) {
      setStep(
        mode === 'create'
          ? { kind: 'new-password', user: null }
          : { kind: 'reauth' }
      )
      return
    }
    setPassword('')
    setCode('')
    setError(null)
    signOutIdp()
  }, [open, mode])

  const run = async (work: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await work()
    } catch (err) {
      const errCode = err instanceof IdpError ? err.code : 'unknown'
      setError(errCode === 'cancelled' ? null : errCode)
    } finally {
      setBusy(false)
    }
  }

  const link = async (user: User) => {
    await addPasswordToAccount(user, password)
    await signOutIdp()
    onChanged()
    setStep({ kind: 'done' })
  }

  const onCode = (e: FormEvent) => {
    e.preventDefault()
    if (step.kind !== 'code') return
    run(async () => link(await resolveTotp(step.resolver, code.trim())))
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (step.kind !== 'new-password') return
    if (linking) {
      // The submit tap opens Google's popup; Safari blocks it otherwise.
      run(async () => {
        const result = await reauthWithGoogle(email)
        if (result.kind === 'totp-required') {
          setStep({ kind: 'code', resolver: result.resolver })
          return
        }
        await link(result.user)
      })
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (mode === 'create') {
        await createPasswordAccount(email, password)
      } else if (step.user) {
        await changePassword(step.user, password)
        await signOutIdp()
      }
      onChanged()
      setStep({ kind: 'done' })
    } catch (err) {
      setError(err instanceof IdpError ? err.code : 'unknown')
    } finally {
      setBusy(false)
    }
  }

  const title =
    mode === 'create'
      ? t('profile.create-password.title')
      : t('profile.change-password.title')

  let submitLabel = t('auth.password-reset.save')
  if (linking) {
    submitLabel = busy
      ? t('auth.password.creating')
      : t('profile.create-password.confirm-google')
  } else if (mode === 'create') {
    submitLabel = busy ? t('auth.password.creating') : t('auth.password.create')
  } else if (busy) submitLabel = t('common.saving')

  return (
    <Sheet open={open} onClose={onClose} title={title}>
      {step.kind === 'reauth' && (
        <ReauthForm
          email={email}
          intro={t('profile.change-password.intro')}
          passwordLabel={t('profile.change-password.current')}
          onVerified={async (user) => {
            setStep({ kind: 'new-password', user })
          }}
        />
      )}

      {step.kind === 'new-password' && (
        <form
          key="new-password"
          onSubmit={onSubmit}
          className="flex flex-col gap-4"
        >
          {mode === 'create' && (
            <p className="text-sm text-ink-700">
              {t('profile.create-password.intro', { email })}
            </p>
          )}
          <Field
            label={t('auth.password-reset.new-password')}
            hint={t('auth.password.password-hint')}
          >
            <Input
              type="password"
              autoComplete="new-password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              required
            />
          </Field>
          {error && (
            <p role="alert" className="text-sm text-flag">
              {t(idpErrorKey(error), { email })}
            </p>
          )}
          <Button type="submit" fullWidth disabled={busy}>
            {submitLabel}
          </Button>
        </form>
      )}

      {step.kind === 'code' && (
        <form key="code" onSubmit={onCode} className="flex flex-col gap-4">
          <p className="text-sm text-ink-700">
            {t('auth.password.totp-intro')}
          </p>
          <OneTimeCodeField value={code} onChange={setCode} />
          {error && (
            <p role="alert" className="text-sm text-flag">
              {t(idpErrorKey(error), { email })}
            </p>
          )}
          <Button type="submit" fullWidth disabled={busy || code.length < 6}>
            {busy ? t('auth.password.creating') : t('auth.password.verify')}
          </Button>
        </form>
      )}

      {step.kind === 'done' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ink-700">
            {mode === 'change' && t('profile.change-password.done')}
            {mode === 'create' &&
              (linking
                ? t('profile.create-password.done-linked', { email })
                : t('profile.create-password.check-email', { email }))}
          </p>
          <Button variant="secondary" fullWidth onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      )}
    </Sheet>
  )
}

export default PasswordSheet
